// Thin fetch wrapper for the same-origin API. Errors carry the server's message for display.
import { FEED_DELETED_NAME } from '@gaintplay/core/names';
import { trackPress } from './press';
import { deviceHeader } from './device';
import { reportAccountBlocked } from './account-block';
import { fmtAgo, fmtNumber, t } from '../i18n';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Machine-readable reason some endpoints add, e.g. "CASHOUT_IN_PROGRESS". */
    public code?: string,
  ) {
    super(message);
  }
}

export function api<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  // A button press that starts a request shows as busy until it settles (see lib/press.ts).
  return trackPress(request<T>(path, init));
}

/** Reads src/boot.ts started before the app loaded, by URL. Each is used once, and only while
 *  it's fresh: later reads go to the server. */
type EarlyReads = Record<string, { at: number; res: Promise<Response> }>;
const EARLY_MAX_AGE = 10_000;

/** The response to a GET of `url` that boot.ts already started, if any (taken: used once). */
function takeEarly(url: string): Promise<Response> | undefined {
  const early = (globalThis as { __gpEarly?: EarlyReads }).__gpEarly;
  const hit = early?.[url];
  if (!hit) return undefined;
  delete early[url];
  return performance.now() - hit.at < EARLY_MAX_AGE ? hit.res : undefined;
}

/** fetch() for a same-origin GET, picking up boot.ts's early read of it when there is one. */
export async function earlyFetch(url: string): Promise<Response> {
  const early = takeEarly(url);
  if (early) {
    try {
      return await early;
    } catch {
      /* the early read failed (network): ask again below */
    }
  }
  return fetch(url, { credentials: 'same-origin' });
}

async function request<T>(
  path: string,
  init: { method?: string; body?: unknown } = {},
): Promise<T> {
  const method = init.method ?? (init.body !== undefined ? 'POST' : 'GET');
  const early = method === 'GET' ? takeEarly(`/api${path}`) : undefined;
  const res = (early && (await early.catch(() => null))) || (await send(path, method, init.body));
  const data = (await res.json().catch(() => ({}))) as {
    error?: string;
    code?: string;
    block?: unknown;
  };
  // A blocked account: the blocked-account screen takes over (components/AccountGate.tsx).
  if (res.status === 403 && data.code === 'ACCOUNT_BLOCKED') reportAccountBlocked(data.block);
  if (!res.ok)
    throw new ApiError(
      res.status,
      // i18n: shown when the server answered with an error but no message; {status} is a number like 503
      data.error ??
        t('Something went wrong ({status}). Please try again.', { status: String(res.status) }),
      data.code,
    );
  return data as T;
}

async function send(path: string, method: string, body: unknown): Promise<Response> {
  const headers: Record<string, string> = {};
  const device = await deviceHeader();
  if (device) headers['x-device'] = device;
  const req: RequestInit = { method, credentials: 'same-origin', headers };
  if (body !== undefined) {
    headers['content-type'] = 'application/json';
    req.body = JSON.stringify(body);
  }
  return fetch(`/api${path}`, req);
}

/** Coins as US dollars, written the reader's way ($2.50, 2,50 $). */
export const coinsToUsd = (coins: number) =>
  fmtNumber(coins / 1000, { style: 'currency', currency: 'USD' });
/** A coin amount the reader's way (12,500 · 12.500 · 12 500). */
export const fmtCoins = (coins: number) => fmtNumber(coins);

/** A live-feed item's name as shown: the username, or the deleted-account placeholder translated. */
export const feedName = (username: string): string =>
  username === FEED_DELETED_NAME ? t(FEED_DELETED_NAME) : username;

export interface Balance {
  available: number;
  pending: number;
  withdrawing: number;
}

export interface Me {
  user: {
    id: string;
    email: string;
    username: string;
    displayName: string | null;
    avatarUrl: string | null;
    countryCode: string | null;
    status: string;
    level: number;
    xp: number;
    referralCode: string;
    emailVerifiedAt: number | null;
    createdAt: number;
    /** "Show my name on the live feed and leaderboard" (on unless the user turned it off). */
    publicName?: boolean;
    /** When the new-member walkthrough was finished; null: show it (components/Guide.tsx). */
    guideDoneAt?: number | null;
  };
  balance: Balance;
  /** An identity check retake waits for them, with or without a cash-out waiting. */
  kycRetake?: boolean;
}

/** How long ago, short, in the reader's language ("5m ago"). */
export const timeAgo = (t: number): string => fmtAgo(t);
